import { useEffect, useState, useCallback } from "react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import {
  Briefcase,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Mail,
  Send,
  Phone,
  Clock,
  Trash2,
  PartyPopper,
  HeartHandshake,
  CalendarClock,
} from "lucide-react";

const FIELD_LABELS = {
  name: "Full Name",
  is_adult: "18 years or older?",
  address: "Street Address",
  city_state: "City / State",
  zip_code: "Zip / Postal Code",
  phone: "Phone",
  email: "Email Address",
  position: "Position Applied For",
  salary_desired: "Salary Desired",
  start_date: "Available to Begin Work",
  education_school: "School Name & Address",
  education_years: "Years Completed",
  education_degree: "Major or Degree",
  convicted: "Convicted of a crime/felony?",
  convicted_explain: "Conviction Explanation",
  drivers_license: "Valid Driver's License?",
  license_state: "License State of Issue",
  employer_name: "Most Recent Employer",
  supervisor: "Last Supervisor",
  employed_from: "Employed From",
  employed_to: "Employed To",
  employer_phone: "Employer Phone",
  job_title: "Last Job Title",
  reason_leaving: "Reason for Leaving",
  duties: "Jobs Held / Duties / Skills",
  may_contact: "May we contact this employer?",
  ref1_name: "Reference 1 — Name",
  ref1_phone: "Reference 1 — Phone",
  ref1_relationship: "Reference 1 — Relationship",
  ref1_years: "Reference 1 — Years Known",
  ref2_name: "Reference 2 — Name",
  ref2_phone: "Reference 2 — Phone",
  ref2_relationship: "Reference 2 — Relationship",
  ref2_years: "Reference 2 — Years Known",
  additional_info: "Additional Qualifications",
  signature: "Signature",
};

const STATUS_STYLES = {
  new: "bg-amber-100 text-amber-800 border-amber-300",
  acknowledged: "bg-sky-100 text-sky-800 border-sky-300",
  responded: "bg-emerald-100 text-emerald-800 border-emerald-300",
  hired: "bg-stone-900 text-brand-yellow border-stone-900",
  interview: "bg-violet-100 text-violet-800 border-violet-300",
  declined: "bg-stone-200 text-stone-600 border-stone-300",
};

const toLocalInput = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const InterviewForm = ({ app, token, onDone, onCancel }) => {
  const [toEmail, setToEmail] = useState(app.email || "");
  const [when, setWhen] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    d.setHours(14, 0, 0, 0);
    return toLocalInput(d);
  });
  const [location, setLocation] = useState("the Blossom's Kitchen food truck");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const invite = async () => {
    if (!toEmail.trim() || !toEmail.includes("@")) {
      toast.error("Enter the applicant's email address");
      return;
    }
    if (!when) {
      toast.error("Pick a date and time");
      return;
    }
    setSending(true);
    try {
      const { data } = await adminApi(token).post(`/admin/job-applications/${app.id}/interview`, {
        to_email: toEmail.trim(),
        when,
        location,
        note,
      });
      toast.success(`Interview invite sent — ${data.when}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the invitation");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-2 bg-violet-50 border border-violet-200 rounded-xl p-3" data-testid={`job-app-interview-form-${app.id}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-violet-700 flex items-center gap-1.5">
        <CalendarClock className="w-3.5 h-3.5" /> Invite {app.name?.split(" ")[0] || "applicant"} to interview
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="text-xs text-stone-600 space-y-1">
          <span className="font-bold">Date & time</span>
          <input
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            data-testid={`job-app-interview-when-${app.id}`}
            className="w-full px-3 py-2 rounded-lg border-2 border-stone-200 bg-white text-sm outline-none focus:border-violet-500"
          />
        </label>
        <label className="text-xs text-stone-600 space-y-1">
          <span className="font-bold">Where</span>
          <input
            value={location}
            onChange={(e) => setLocation(e.target.value.slice(0, 200))}
            data-testid={`job-app-interview-location-${app.id}`}
            className="w-full px-3 py-2 rounded-lg border-2 border-stone-200 bg-white text-sm outline-none focus:border-violet-500"
          />
        </label>
      </div>
      <input
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, 500))}
        placeholder="Optional note — e.g. Ask for Blossom, bring your ID"
        data-testid={`job-app-interview-note-${app.id}`}
        className="w-full px-3 py-2 rounded-lg border-2 border-stone-200 bg-white text-sm outline-none focus:border-violet-500"
      />
      {!app.email && (
        <p className="text-[11px] text-amber-700">No email on file — type the applicant&apos;s email address below.</p>
      )}
      <input
        type="email"
        value={toEmail}
        onChange={(e) => setToEmail(e.target.value)}
        data-testid={`job-app-interview-to-${app.id}`}
        placeholder="applicant@email.com"
        className="w-full px-3 py-2 rounded-lg border-2 border-stone-200 bg-white outline-none focus:border-violet-500 text-sm"
      />
      <div className="flex gap-2">
        <button
          onClick={invite}
          disabled={sending}
          data-testid={`job-app-interview-confirm-${app.id}`}
          className="flex-1 bg-violet-700 text-white px-5 py-2.5 rounded-full text-xs font-black uppercase tracking-widest hover:bg-violet-800 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Send className="w-3.5 h-3.5" /> {sending ? "Sending..." : "Send Invitation"}
        </button>
        <button
          onClick={onCancel}
          disabled={sending}
          data-testid={`job-app-interview-cancel-${app.id}`}
          className="px-4 py-2.5 rounded-full text-xs font-bold text-stone-500 hover:text-stone-800"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const RespondForm = ({ app, token, onSent }) => {
  const [subject, setSubject] = useState(
    `Your application at Blossom's Kitchen — ${app.position || ""}`.trim()
  );
  const [message, setMessage] = useState("");
  const [toEmail, setToEmail] = useState(app.email || "");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error("Please fill in both the subject and the message");
      return;
    }
    if (!toEmail.trim() || !toEmail.includes("@")) {
      toast.error("Enter the applicant's email address");
      return;
    }
    setSending(true);
    try {
      await adminApi(token).post(`/admin/job-applications/${app.id}/respond`, {
        subject,
        message,
        to_email: toEmail.trim(),
      });
      toast.success(`Email sent to ${toEmail.trim()}`);
      setMessage("");
      onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Email could not be sent");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-2 bg-stone-50 border border-stone-200 rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 flex items-center gap-1.5">
        <Mail className="w-3.5 h-3.5" /> Email {app.name}
      </div>
      {!app.email && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
          This application was submitted before we collected emails — type the
          applicant&apos;s email address below (ask them by phone if needed).
        </p>
      )}
      <input
        type="email"
        value={toEmail}
        onChange={(e) => setToEmail(e.target.value)}
        data-testid={`job-app-respond-to-${app.id}`}
        placeholder="applicant@email.com"
        className="w-full px-3 py-2 rounded-lg border-2 border-stone-300 bg-white outline-none focus:border-brand-red text-sm"
      />
      <input
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        data-testid={`job-app-respond-subject-${app.id}`}
        placeholder="Subject"
        className="w-full px-3 py-2 rounded-lg border-2 border-stone-300 bg-white outline-none focus:border-brand-red text-sm"
      />
      <textarea
        rows={4}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        data-testid={`job-app-respond-message-${app.id}`}
        placeholder={`Hi ${app.name?.split(" ")[0] || "there"}, thanks for applying...`}
        className="w-full px-3 py-2 rounded-lg border-2 border-stone-300 bg-white outline-none focus:border-brand-red text-sm"
      />
      <button
        onClick={send}
        disabled={sending}
        data-testid={`job-app-respond-send-${app.id}`}
        className="w-full sm:w-auto bg-brand-red text-white px-5 py-2.5 rounded-full text-xs font-black uppercase tracking-widest hover:bg-red-800 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <Send className="w-3.5 h-3.5" /> {sending ? "Sending..." : "Send Email"}
      </button>
    </div>
  );
};

const HireForm = ({ app, token, onHired, onCancel }) => {
  const [toEmail, setToEmail] = useState(app.email || "");
  const [sending, setSending] = useState(false);

  const hire = async () => {
    if (!toEmail.trim() || !toEmail.includes("@")) {
      toast.error("Enter the applicant's email address");
      return;
    }
    setSending(true);
    try {
      const { data } = await adminApi(token).post(`/admin/job-applications/${app.id}/hire`, {
        to_email: toEmail.trim(),
      });
      toast.success(`${app.name || "Applicant"} marked hired — welcome email sent to ${data.to}`);
      onHired();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the welcome email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-2 bg-stone-900 text-white rounded-xl p-3" data-testid={`job-app-hire-form-${app.id}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-brand-yellow flex items-center gap-1.5">
        <PartyPopper className="w-3.5 h-3.5" /> Hire {app.name?.split(" ")[0] || "applicant"}
      </div>
      <p className="text-xs text-stone-300">
        This sends the welcome email right away:
      </p>
      <blockquote className="text-sm italic border-l-2 border-brand-yellow pl-3 text-stone-100">
        “Welcome to the Blossom's family — we're so happy to have you! Let's grow together. See you on your first day!”
      </blockquote>
      {!app.email && (
        <p className="text-[11px] text-amber-200">
          No email on file — type the applicant&apos;s email address below.
        </p>
      )}
      <input
        type="email"
        value={toEmail}
        onChange={(e) => setToEmail(e.target.value)}
        data-testid={`job-app-hire-to-${app.id}`}
        placeholder="applicant@email.com"
        className="w-full px-3 py-2 rounded-lg border-2 border-stone-600 bg-stone-800 text-white outline-none focus:border-brand-yellow text-sm"
      />
      <div className="flex gap-2">
        <button
          onClick={hire}
          disabled={sending}
          data-testid={`job-app-hire-confirm-${app.id}`}
          className="flex-1 bg-brand-yellow text-stone-900 px-5 py-2.5 rounded-full text-xs font-black uppercase tracking-widest hover:brightness-105 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Send className="w-3.5 h-3.5" /> {sending ? "Sending..." : "Confirm Hire & Send"}
        </button>
        <button
          onClick={onCancel}
          disabled={sending}
          data-testid={`job-app-hire-cancel-${app.id}`}
          className="px-4 py-2.5 rounded-full text-xs font-bold text-stone-300 hover:text-white"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const DECLINE_PREVIEW =
  "Thank you so much for taking the time to apply… After careful consideration, we've decided to move forward with other candidates for this position right now. This isn't a reflection of your abilities — we simply had more great applicants than openings. Wishing you all the best, The Blossom's Kitchen Family";

const DeclineForm = ({ app, token, onDone, onCancel }) => {
  const [toEmail, setToEmail] = useState(app.email || "");
  const [sending, setSending] = useState(false);

  const decline = async () => {
    if (!toEmail.trim() || !toEmail.includes("@")) {
      toast.error("Enter the applicant's email address");
      return;
    }
    setSending(true);
    try {
      const { data } = await adminApi(token).post(`/admin/job-applications/${app.id}/decline`, {
        to_email: toEmail.trim(),
      });
      toast.success(`Kind decline email sent to ${data.to}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-2 bg-stone-100 border border-stone-300 rounded-xl p-3" data-testid={`job-app-decline-form-${app.id}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-stone-600 flex items-center gap-1.5">
        <HeartHandshake className="w-3.5 h-3.5" /> Not this time — {app.name?.split(" ")[0] || "applicant"}
      </div>
      <p className="text-xs text-stone-500">This sends a warm, respectful decline right away:</p>
      <blockquote className="text-sm italic border-l-2 border-stone-400 pl-3 text-stone-700">“{DECLINE_PREVIEW}”</blockquote>
      {!app.email && (
        <p className="text-[11px] text-amber-700">No email on file — type the applicant&apos;s email address below.</p>
      )}
      <input
        type="email"
        value={toEmail}
        onChange={(e) => setToEmail(e.target.value)}
        data-testid={`job-app-decline-to-${app.id}`}
        placeholder="applicant@email.com"
        className="w-full px-3 py-2 rounded-lg border-2 border-stone-300 bg-white outline-none focus:border-brand-red text-sm"
      />
      <div className="flex gap-2">
        <button
          onClick={decline}
          disabled={sending}
          data-testid={`job-app-decline-confirm-${app.id}`}
          className="flex-1 bg-stone-700 text-white px-5 py-2.5 rounded-full text-xs font-black uppercase tracking-widest hover:bg-stone-800 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Send className="w-3.5 h-3.5" /> {sending ? "Sending..." : "Send Decline Email"}
        </button>
        <button
          onClick={onCancel}
          disabled={sending}
          data-testid={`job-app-decline-cancel-${app.id}`}
          className="px-4 py-2.5 rounded-full text-xs font-bold text-stone-500 hover:text-stone-800"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const ApplicationCard = ({ app, token, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [replying, setReplying] = useState(false);
  const [hiring, setHiring] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [interviewing, setInterviewing] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const status = app.status || "new";
  const closeAll = () => { setReplying(false); setHiring(false); setDeclining(false); setInterviewing(false); };

  const acknowledge = async () => {
    try {
      await adminApi(token).post(`/admin/job-applications/${app.id}/acknowledge`);
      toast.success("Application acknowledged");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not acknowledge");
    }
  };

  const clearApp = async () => {
    if (!confirmClear) {
      setConfirmClear(true);
      setTimeout(() => setConfirmClear(false), 4000);
      return;
    }
    try {
      await adminApi(token).delete(`/admin/job-applications/${app.id}`);
      toast.success("Application cleared");
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not clear application");
    }
  };

  return (
    <div
      className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden"
      data-testid={`job-app-card-${app.id}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`job-app-toggle-${app.id}`}
        className="w-full px-4 py-3 flex items-center gap-3 text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="font-black text-stone-800 truncate">{app.name || "—"}</div>
          <div className="text-xs text-stone-500 truncate">
            {app.position || "—"} · {fmtDate(app.submitted_at)}
          </div>
        </div>
        <span
          data-testid={`job-app-status-${app.id}`}
          className={`shrink-0 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border ${STATUS_STYLES[status] || STATUS_STYLES.new}`}
        >
          {status}
        </span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-stone-400 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-stone-400 shrink-0" />
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex flex-wrap gap-2 text-xs">
            {app.phone && (
              <a
                href={`tel:${app.phone}`}
                className="flex items-center gap-1.5 bg-stone-100 px-3 py-1.5 rounded-full font-bold text-stone-700"
              >
                <Phone className="w-3.5 h-3.5" /> {app.phone}
              </a>
            )}
            {app.email && (
              <span className="flex items-center gap-1.5 bg-stone-100 px-3 py-1.5 rounded-full font-bold text-stone-700">
                <Mail className="w-3.5 h-3.5" /> {app.email}
              </span>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm border border-stone-200 rounded-xl p-3">
            {Object.entries(FIELD_LABELS).map(([k, label]) =>
              app[k] ? (
                <div key={k} className="flex flex-col">
                  <span className="text-[9px] uppercase tracking-widest font-bold text-stone-400">
                    {label}
                  </span>
                  <span className="text-stone-800 break-words">{app[k]}</span>
                </div>
              ) : null
            )}
          </div>

          {(app.responses || []).length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">
                Emails sent to applicant
              </div>
              {app.responses.map((r, i) => (
                <div key={i} className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm">
                  <div className="font-bold text-emerald-900">{r.subject}</div>
                  <div className="text-stone-700 whitespace-pre-wrap">{r.message}</div>
                  <div className="text-[10px] text-stone-400 mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {fmtDate(r.sent_at)}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {status === "new" && (
              <button
                onClick={acknowledge}
                data-testid={`job-app-ack-${app.id}`}
                className="flex items-center gap-1.5 bg-sky-600 text-white px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest hover:bg-sky-700"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Acknowledge
              </button>
            )}
            <button
              onClick={() => { const v = !replying; closeAll(); setReplying(v); }}
              data-testid={`job-app-reply-btn-${app.id}`}
              className="flex items-center gap-1.5 bg-brand-red text-white px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest hover:bg-red-800"
            >
              <Mail className="w-3.5 h-3.5" /> {replying ? "Close" : "Respond by Email"}
            </button>
            {status !== "hired" && status !== "declined" && (
              <button
                onClick={() => { const v = !interviewing; closeAll(); setInterviewing(v); }}
                data-testid={`job-app-interview-btn-${app.id}`}
                className="flex items-center gap-1.5 bg-violet-700 text-white px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest hover:bg-violet-800"
              >
                <CalendarClock className="w-3.5 h-3.5" /> {interviewing ? "Close" : "Invite to Interview"}
              </button>
            )}
            {status !== "hired" && (
              <button
                onClick={() => { const v = !hiring; closeAll(); setHiring(v); }}
                data-testid={`job-app-hire-btn-${app.id}`}
                className="flex items-center gap-1.5 bg-stone-900 text-brand-yellow px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest hover:bg-stone-800"
              >
                <PartyPopper className="w-3.5 h-3.5" /> {hiring ? "Close" : "Hired!"}
              </button>
            )}
            {status !== "hired" && status !== "declined" && (
              <button
                onClick={() => { const v = !declining; closeAll(); setDeclining(v); }}
                data-testid={`job-app-decline-btn-${app.id}`}
                className="flex items-center gap-1.5 bg-white text-stone-600 border-2 border-stone-300 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest hover:border-stone-500"
              >
                <HeartHandshake className="w-3.5 h-3.5" /> {declining ? "Close" : "Not this time"}
              </button>
            )}
            {status !== "new" && (
              <button
                onClick={clearApp}
                data-testid={`job-app-clear-${app.id}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest border-2 ${
                  confirmClear
                    ? "bg-red-600 text-white border-red-600"
                    : "bg-white text-stone-500 border-stone-300 hover:border-red-400 hover:text-red-600"
                }`}
              >
                <Trash2 className="w-3.5 h-3.5" /> {confirmClear ? "Tap again to confirm" : "Clear"}
              </button>
            )}
          </div>

          {replying && <RespondForm app={app} token={token} onSent={onChanged} />}
          {hiring && (
            <HireForm
              app={app}
              token={token}
              onHired={() => { setHiring(false); onChanged(); }}
              onCancel={() => setHiring(false)}
            />
          )}
          {declining && (
            <DeclineForm
              app={app}
              token={token}
              onDone={() => { setDeclining(false); onChanged(); }}
              onCancel={() => setDeclining(false)}
            />
          )}
          {interviewing && (
            <InterviewForm
              app={app}
              token={token}
              onDone={() => { setInterviewing(false); onChanged(); }}
              onCancel={() => setInterviewing(false)}
            />
          )}
        </div>
      )}
    </div>
  );
};

const HiringToggle = ({ token }) => {
  const { settings, refresh } = useSiteSettings();
  const [saving, setSaving] = useState(false);
  const open = settings?.hiring_open !== false;

  const flip = async () => {
    setSaving(true);
    try {
      await adminApi(token).put("/site-settings", { hiring_open: !open });
      await refresh();
      toast.success(!open ? "\"We're Hiring\" is back on the storefront" : "Hiring paused — pill hidden from the storefront");
    } catch {
      toast.error("Couldn't change hiring status");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`rounded-2xl border-2 px-4 py-3 flex items-center gap-3 ${open ? "bg-stone-900 border-stone-900 text-white" : "bg-white border-stone-300 text-stone-700"}`}
      data-testid="hiring-toggle-card"
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold flex items-center gap-2">
          <Briefcase className={`w-4 h-4 ${open ? "text-brand-yellow" : "text-stone-400"}`} />
          {open ? "We're Hiring — pill is showing" : "Hiring paused — pill hidden"}
        </div>
        <div className={`text-xs ${open ? "text-stone-300" : "text-stone-500"}`}>
          {open ? "Customers see the \"We're Hiring\" button on the storefront." : "Flip it back on whenever a position opens up."}
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={open}
        onClick={flip}
        disabled={saving}
        data-testid="hiring-toggle-btn"
        className={`relative w-14 h-8 rounded-full transition-colors disabled:opacity-50 ${open ? "bg-brand-yellow" : "bg-stone-300"}`}
      >
        <span className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-all ${open ? "left-7" : "left-1"}`} />
      </button>
    </div>
  );
};

export const JobApplicationsTab = ({ token }) => {
  const [apps, setApps] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi(token).get("/admin/job-applications");
      setApps(data);
    } catch {
      toast.error("Could not load applications");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const newCount = (apps || []).filter((a) => (a.status || "new") === "new").length;

  return (
    <div className="space-y-3" data-testid="job-applications-tab">
      <div className="flex items-center justify-between">
        <div className="font-display text-brand-red text-2xl flex items-center gap-2">
          <Briefcase className="w-5 h-5" /> Job Applications
          {newCount > 0 && (
            <span className="bg-brand-yellow text-brand-red text-xs font-black px-2 py-0.5 rounded-full">
              {newCount} new
            </span>
          )}
        </div>
        <button
          onClick={load}
          data-testid="job-apps-refresh-btn"
          className="text-stone-500 hover:text-brand-red"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <HiringToggle token={token} />

      {apps === null ? (
        <div className="text-sm text-stone-500 py-8 text-center">Loading applications…</div>
      ) : apps.length === 0 ? (
        <div
          className="bg-white rounded-2xl border border-dashed border-stone-300 py-12 text-center text-sm text-stone-500"
          data-testid="job-apps-empty"
        >
          No job applications yet. When someone applies at /jobs it will show up here.
        </div>
      ) : (
        apps.map((app) => (
          <ApplicationCard key={app.id} app={app} token={token} onChanged={load} />
        ))
      )}
    </div>
  );
};

export default JobApplicationsTab;
